

































































































import { Component, Prop, Vue, Watch } from "vue-property-decorator";
import moment from "moment";
import TandaVitalSvc from "@/services/TandaVital";
import Monitoring24JamIcuSvc from "@/services/Monitoring24JamIcu";
import { appModule } from "@/store/modules/app";

const KESADARAN_MAP: Record<string, string> = {
  cm: "CM",
  apatis: "Apatis",
  somnolen: "Somnolen",
  sopor: "Sopor",
  koma: "Koma",
  coma: "Koma"
};

const NYERI_METODE_VALID = ["nrs", "flacc", "nips", "bps"];

interface TtvCandidate {
  id: number;
  jam: number;
  menit: number;
  waktuLabel: string;
  tdText: string;
  sistole: number | null;
  diastole: number | null;
  hr: number | null;
  temp: number | null;
  rr: number | null;
  spo2: number | null;
  kesadaran: string | null;
  gcsText: string;
  gcs: { e: number | null; v: number | null; m: number | null };
  nyeriSkor: number | null;
  nyeriMetode: string | null;
  sudahTertaut: boolean;
  terkunci: boolean;
}

@Component({
  inject: { isReadOnly: { default: () => () => false } }
})
export default class Monitoring24JamIcuTtvImportDialog extends Vue {
  @Prop() value!: boolean;
  @Prop() monitoringId!: number;
  @Prop() registerId!: string | number;
  @Prop() tanggal!: string;
  @Prop({ default: () => [] }) hourly!: any[];

  isReadOnly!: () => boolean;
  get readOnly(): boolean {
    return this.isReadOnly();
  }

  loadingTtv = false;
  saving = false;
  candidates: TtvCandidate[] = [];
  selected: number[] = [];

  get show() {
    return this.value;
  }
  set show(val: boolean) {
    this.$emit("input", val);
  }

  @Watch("value")
  async onOpen(val: boolean) {
    if (!val) return;
    this.selected = [];
    await this.fetchTtv();
  }

  nilai(v: number | null) {
    return v === null || v === undefined ? "—" : v;
  }

  numOrNull(v: any): number | null {
    if (v === null || v === undefined || v === "") return null;
    const n = Number(v);
    return isNaN(n) ? null : n;
  }

  async fetchTtv() {
    this.loadingTtv = true;
    try {
      const res: any = await TandaVitalSvc.getByRegister(
        String(this.registerId)
      ).catch(() => null);
      const rows: any[] = res && res.data ? res.data : res || [];
      this.candidates = this.buildCandidates(Array.isArray(rows) ? rows : []);
    } finally {
      this.loadingTtv = false;
    }
  }

  buildCandidates(rows: any[]): TtvCandidate[] {
    const tglForm = String(this.tanggal || "").slice(0, 10);
    if (!tglForm) return [];
    const tglBesok = moment(tglForm, "YYYY-MM-DD")
      .add(1, "day")
      .format("YYYY-MM-DD");

    // Kandidat yang id-nya sudah tertaut ke baris grid (hasil impor
    // sebelumnya ATAU baris TTV bentukan bridge dari grid) dikunci —
    // menutup gema dua arah impor ⇄ write-back (D-110)
    const ttvTertaut = new Set<number>();
    for (const h of this.hourly || []) {
      if (h && h.tanda_vital_id) {
        ttvTertaut.add(Number(h.tanda_vital_id));
      }
    }

    const out: TtvCandidate[] = [];

    const dalamJendela = rows.filter((r: any) => {
      const w = String(r.waktu_periksa || "");
      const tgl = w.slice(0, 10);
      const jm = w.slice(11, 16);
      if (!/^\d{4}-\d{2}-\d{2}$/.test(tgl) || !/^\d{2}:\d{2}$/.test(jm)) {
        return false;
      }
      const jam = Number(jm.slice(0, 2));
      return (tgl === tglForm && jam >= 6) || (tgl === tglBesok && jam < 6);
    });

    dalamJendela.sort((a: any, b: any) =>
      String(a.waktu_periksa).localeCompare(String(b.waktu_periksa))
    );

    for (const r of dalamJendela) {
      const w = String(r.waktu_periksa);
      const jam = Number(w.slice(11, 13));
      const menit = Number(w.slice(14, 16));
      const sudahTertaut = ttvTertaut.has(Number(r.id));

      const gcsE = this.numOrNull(r.gcs_e);
      const gcsV = this.numOrNull(r.gcs_v);
      const gcsM = this.numOrNull(r.gcs_m);
      // Monitor TTV menyimpan NT sebagai 0 (KesadaranGCSInput id 0)
      const gcsTampil = (v: number | null) =>
        v === null ? "-" : v === 0 ? "NT" : String(v);
      const gcsText =
        gcsE !== null || gcsV !== null || gcsM !== null
          ? `E${gcsTampil(gcsE)} V${gcsTampil(gcsV)} M${gcsTampil(gcsM)}`
          : "";

      const sistole = this.numOrNull(r.sistole);
      const diastole = this.numOrNull(r.diastole);
      const tdText =
        sistole !== null || diastole !== null
          ? `${sistole === null ? "" : sistole}/${
              diastole === null ? "" : diastole
            }`
          : "";

      const nyeri = r.nyeri || null;
      const metode = nyeri
        ? String(nyeri.metode || "").toLowerCase()
        : "";

      out.push({
        id: r.id,
        jam,
        menit,
        waktuLabel: `${w.slice(11, 16)} (${moment(w.slice(0, 10)).format(
          "DD/MM"
        )})`,
        tdText,
        sistole,
        diastole,
        hr: this.numOrNull(r.frekuensi_nadi),
        temp: this.numOrNull(r.suhu_badan),
        rr: this.numOrNull(r.frekuensi_nafas),
        spo2: this.numOrNull(r.saturasi_oksigen),
        kesadaran: KESADARAN_MAP[String(r.kesadaran || "").toLowerCase()] || null,
        gcsText,
        gcs: { e: gcsE, v: gcsV, m: gcsM },
        nyeriSkor:
          nyeri && NYERI_METODE_VALID.indexOf(metode) !== -1
            ? this.numOrNull(nyeri.skala)
            : null,
        nyeriMetode:
          nyeri && NYERI_METODE_VALID.indexOf(metode) !== -1 ? metode : null,
        sudahTertaut,
        terkunci: sudahTertaut
      });
    }

    return out;
  }

  // Monitor TTV menyimpan GCS NT sebagai 0 — kirim "NT" ke grid (D-110);
  // ada komponen NT → total "NT" (cermin recalcGcs() dialog hourly)
  gcsKirim(v: number | null): string | null {
    if (v === null) return null;
    return v === 0 ? "NT" : String(v);
  }

  payloadDari(c: TtvCandidate): any {
    const payload: any = {
      jam: c.jam,
      menit: c.menit,
      tanda_vital_id: c.id
    };
    if (c.sistole !== null) payload.td_sistole = c.sistole;
    if (c.diastole !== null) payload.td_diastole = c.diastole;
    if (c.hr !== null) payload.hr = c.hr;
    if (c.temp !== null) payload.temp = c.temp;
    if (c.rr !== null) payload.rr = c.rr;
    if (c.spo2 !== null) payload.spo2 = c.spo2;
    if (c.kesadaran) payload.kesadaran = c.kesadaran;
    const e = this.gcsKirim(c.gcs.e);
    const v = this.gcsKirim(c.gcs.v);
    const m = this.gcsKirim(c.gcs.m);
    if (e !== null) payload.gcs_e = e;
    if (v !== null) payload.gcs_v = v;
    if (m !== null) payload.gcs_m = m;
    if (e !== null && v !== null && m !== null) {
      payload.gcs_total =
        e === "NT" || v === "NT" || m === "NT"
          ? "NT"
          : String(Number(e) + Number(v) + Number(m));
    }
    if (c.nyeriSkor !== null && c.nyeriMetode) {
      payload.skor_nyeri = c.nyeriSkor;
      payload.nyeri_metode = c.nyeriMetode;
    }
    return payload;
  }

  async importRows() {
    if (this.readOnly || !this.selected.length) return;
    const dipilih = this.candidates.filter(
      c => this.selected.indexOf(c.id) !== -1 && !c.terkunci
    );
    if (!dipilih.length) return;
    this.saving = true;
    let sukses = 0;
    try {
      for (const c of dipilih) {
        const resp = await Monitoring24JamIcuSvc.upsertHourly(
          this.monitoringId,
          this.payloadDari(c)
        ).catch(() => null);
        if (resp && resp.data && resp.data.status === "1") sukses++;
      }
      if (sukses > 0) {
        appModule.NoticeDelay(
          `${sukses} kolom jam ditambahkan dari Monitor TTV`
        );
        this.$emit("saved");
        this.close();
      } else {
        appModule.NoticeDelay("Tidak ada data yang berhasil diimpor");
      }
    } finally {
      this.saving = false;
    }
  }

  close() {
    this.show = false;
  }
}
